import { ampli } from '@/ampli';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';

interface SwitchButtonProps {
  /** Inactive token  */
  inactiveToken?: TokenResult;
  /** Callback when a token is selected */
  onTokenSelect?: (token: TokenResult) => void;
  /** Active token */
  activeToken: TokenResult | undefined;
  /** Additional class name. */
  className?: string;
}

export const SwitchButton = ({
  inactiveToken,
  onTokenSelect,
  activeToken,
  className,
}: SwitchButtonProps) => {
  const onClick = () => {
    if (activeToken && inactiveToken) {
      onTokenSelect?.(inactiveToken);

      ampli.tokenSwitchClicked({
        token_chain_id: inactiveToken.chainId,
        token_chain_name: inactiveToken.chainName ?? '',
        token_address: inactiveToken.address,
        token_symbol: inactiveToken.symbol ?? '',
      });
    }
  };

  if (!inactiveToken) return null;

  const isCrossChain = activeToken?.chainId !== inactiveToken.chainId;

  return (
    <button
      className={classNames(
        buttons({
          shape: 'pill',
          color: 'gray',
          size: 'small',
          class: [
            'h-8 gap-2 pl-2 pr-2.5',
            fontStyles({ variant: 'LABEL_SM' }),
            'text-token-gray-800 hover:text-token-gray-1600',
          ],
        }),
        className,
      )}
      aria-label={
        isCrossChain
          ? `Switch to ${inactiveToken.symbol} on ${inactiveToken.chainName}`
          : `Switch to ${inactiveToken.symbol}`
      }
      onClick={onClick}
    >
      <TokenAvatar
        address={inactiveToken.address}
        chainId={inactiveToken.chainId}
        size={20}
        showChainLogo={false}
      />
      <span className="flex items-center gap-1">
        Switch to {inactiveToken.symbol}
        {isCrossChain && (
          <>
            <span className="text-token-gray-700">on</span>
            {/* Popover only: a bottom sheet would swallow the tap meant to switch tokens. */}
            <MatchaTooltip>
              <MatchaTooltipTrigger>
                <span className="flex">
                  <ChainAvatar chainId={inactiveToken.chainId} size={16} />
                </span>
              </MatchaTooltipTrigger>
              <MatchaTooltipContent>{inactiveToken.chainName}</MatchaTooltipContent>
              <MatchaTooltipPopover sideOffset={8} />
            </MatchaTooltip>
          </>
        )}
      </span>
    </button>
  );
};
